import axios from 'axios';
import { getValidToken, renewToken, isTokenExpired } from './authFetch';

const axiosServices = axios.create();

// Interceptor de petición para axiosServices
axiosServices.interceptors.request.use(
    async (config) => {
        try {
            const token = await getValidToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } catch (err) {
            console.warn('[axiosServices] No se pudo obtener token válido:', err);
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor de respuesta para reintentar en 401/403
axiosServices.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403) &&
            originalRequest &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;
            try {
                const newToken = await renewToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axiosServices(originalRequest);
                }
            } catch (retryErr) {
                console.error('[axiosServices] Falló reintento tras renovar:', retryErr);
            }
        }
        return Promise.reject((error.response && error.response.data) || error);
    }
);

// Aplicar también a la instancia global de axios para proteger llamadas directas
// (ej. bonificaciones_admin, solicitudes_departamento, etc.)
axios.interceptors.request.use(
    async (config) => {
        const url = config.url || '';
        // No interceptar peticiones de login o refresh
        if (url.includes('users/login') || url.includes('users/refresh') || url.includes('users/update-pass') || url.includes('users/change-pass')) {
            return config;
        }

        const currentAuth = config.headers?.Authorization || config.headers?.authorization;
        // Si no hay Authorization, o si el token que lleva está expirado o próximo a expirar
        const rawToken = currentAuth ? currentAuth.replace(/^Bearer\s+/i, '') : null;
        if (!currentAuth || isTokenExpired(rawToken)) {
            try {
                const token = await getValidToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (err) {
                console.warn('[axios global] Error al refrescar token:', err);
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const url = originalRequest?.url || '';
        if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403) &&
            originalRequest &&
            !originalRequest._retry &&
            !url.includes('users/login') &&
            !url.includes('users/refresh')
        ) {
            originalRequest._retry = true;
            try {
                const newToken = await renewToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axios(originalRequest);
                }
            } catch (retryErr) {
                console.error('[axios global] Falló reintento tras renovar:', retryErr);
            }
        }
        return Promise.reject(error);
    }
);

export default axiosServices;
